import { assign, createMachine } from 'xstate'

import type { GetCustomerRequestSellerActionsResponse } from '~/api/care-folders/types/seller-actions'
import type { OrderlineRequest } from '~/api/seller-after-sales/types'

import type { MachineContext, MachineEvents } from './ActionBar.machine.type'
import { storeActionsAndRequests } from './actions/storeActionsAndRequests'

export const actionBarMachine = createMachine<MachineContext, MachineEvents>(
  {
    id: 'seller-action-bar',
    context: {} as MachineContext,
    predictableActionArguments: true,
    initial: 'initialFetch',
    schema: {
      services: {
        fetchActionsAndRequests: {} as {
          data: [GetCustomerRequestSellerActionsResponse, OrderlineRequest[]]
        },
      },
      events: {} as MachineEvents,
    },
    states: {
      initialFetch: {
        id: 'initialFetch',
        initial: 'loading',
        states: {
          loading: {
            invoke: {
              id: 'fetchActionsAndRequests',
              src: 'fetchActionsAndRequests',
              onDone: {
                actions: assign(storeActionsAndRequests),
                target: 'loaded',
              },
              onError: {
                target: 'error',
              },
            },
          },
          error: {
            on: {
              TRY_AGAIN: 'loading',
            },
          },
          loaded: { type: 'final' },
        },
        onDone: {
          target: 'actionsList',
        },
      },
      actionsList: {
        initial: 'mainActions',
        on: {
          START_SHIP_BACK_FLOW: 'shipBackFlow',
          START_REFUND_FLOW: 'refundFlow',
          START_REPORT_PROBLEM_FLOW: {
            actions: assign((context) => ({
              reportProblemInfo: {
                ...context.reportProblemInfo,
                hasSelectedSendMessage: false,
              },
            })),
            target: 'reportProblemFlow',
          },
          START_SEND_MESSAGE_FLOW: [
            {
              cond: 'shouldReportProblem',
              actions: assign((context) => ({
                reportProblemInfo: {
                  ...context.reportProblemInfo,
                  hasSelectedSendMessage: true,
                },
              })),
              target: 'reportProblemFlow',
            },
            { target: 'sendMessageFlow' },
          ],
        },
        states: {
          mainActions: {
            on: { OTHER_ACTIONS: 'otherActions' },
          },
          otherActions: {
            on: {
              BACK: 'mainActions',
              EXIT: 'mainActions',
            },
          },
        },
      },
      shipBackFlow: {
        on: {
          BACK: 'actionsList',
          DONE: 'initialFetch',
          EXIT: 'actionsList',
        },
      },
      refundFlow: {
        initial: 'amountStep',
        on: {
          BACK: 'actionsList',
          EXIT: 'actionsList',
          DONE: 'initialFetch',
        },
        states: {
          amountStep: {
            on: {
              PARTIAL_REFUND_INITIATED: 'partialRefundStep',
            },
          },
          partialRefundStep: {
            on: {
              BACK: 'amountStep',
            },
          },
        },
      },
      sendMessageFlow: {
        on: {
          BACK: 'actionsList',
          EXIT: 'actionsList',
          DONE: 'initialFetch',
        },
      },
      reportProblemFlow: {
        initial: 'problemListStep',
        on: {
          BACK: 'actionsList',
          EXIT: 'actionsList',
          DONE: 'initialFetch',
        },
        states: {
          problemListStep: {
            on: {
              DAMAGED_PACKAGE_SELECTED: 'detailsStep.damagedPackage',
              OOW_ITEM_SELECTED: 'detailsStep.oowItem',
              ITEM_NOT_RECEIVED_SELECTED: 'detailsStep.itemNotReceived',
              ACCOUNT_LOCKED_SELECTED: 'detailsStep.accountLocked',
              CODE_LOCKED_SELECTED: 'detailsStep.codeLocked',
              MISSING_PART_SELECTED: 'detailsStep.missingPart',
              WRONG_ITEM_SELECTED: 'detailsStep.wrongItem',
              PROBLEM_NOT_IDENTIFIED_SELECTED:
                'detailsStep.problemNotIdentified',
              OTHER_SELECTED: 'detailsStep.other',
            },
          },
          detailsStep: {
            on: {
              BACK: 'problemListStep',
            },
            states: {
              damagedPackage: {},
              oowItem: {},
              itemNotReceived: {},
              accountLocked: {},
              codeLocked: {},
              missingPart: {},
              wrongItem: {},
              problemNotIdentified: {},
              other: {},
            },
          },
        },
      },
    },
  },
  {
    guards: {
      shouldReportProblem: (context) =>
        context.sendMessageInfo.shouldReportProblem || false,
    },
  },
)
